<template>
  <v-dialog
    v-model="isOpen"
    max-width="800"
    @input="handleClose"
  >
    <v-card>
      <v-card-title class="headline pb-10">
        <div class="columns">
          <div class="column" v-if="isCreate">
            <h2 class="title is-3">Создать коллекцию</h2>
          </div>
          <div class="column" v-if="!isCreate">
            <h2 class="title is-3">Изменить коллекцию: {{ currentCollectionName }}</h2>
          </div>
        </div>
      </v-card-title>
      <v-card-text>
        <v-layout row>
          <v-layout column>
            <b-field label="Название">
              <b-input v-model="currentCollection.name"></b-input>
            </b-field>


            <div v-if="!isCreate || (!currentCollection.use_standard_fields && isCreate)">

              <div
                class="mt-6"
                style="
                align-items: end;
                display: flex;
                flex-direction: column;"
              >
                <b-button @click="addField" type="is-primary" size="is-small">
                  Добавить поле
                </b-button>
              </div>

              <b-field label="Поля" required>
                <b-table :data="currentCollection.fields" :striped="true" :hoverable="true">
                  <b-table-column field="name" label="Имя поля">
                    <template v-slot="props">
                      <b-input
                        v-model="props.row.name"
                        placeholder="Введите имя поля"
                        required
                      ></b-input>
                    </template>
                  </b-table-column>

                  <b-table-column field="type" label="Тип поля">
                    <template v-slot="props">
                      <b-select v-model="props.row.type">
                        <option value="id">ID</option>
                        <option value="image">Image</option>
                        <option value="link">Link</option>
                        <option value="show">Show</option>
                        <option value="string">String</option>
                        <option value="number">Number</option>
                        <option value="bool">Boolean</option>
                        <option value="timestamp">Timestamp</option>
                      </b-select>
                    </template>
                  </b-table-column>

                  <b-table-column field="default_value" label="По умолчанию">
                    <template v-slot="props">
                      <!-- Для типа number используем input type="number" -->
                      <b-input
                        v-if="props.row.type === 'number'"
                        v-model="props.row.default_value"
                        type="number"
                        required
                      ></b-input>

                      <!-- Для типа bool и show используем тройной чекбокс -->
                      <div v-else-if="props.row.type === 'bool' || props.row.type === 'show'" class="tri-state-checkbox">
                        <b-button
                          type="is-text"
                          @click="toggleTriState(props.row)"
                          :class="{
                            'has-text-grey': props.row.default_value === null || props.row.default_value === '',
                            'has-text-success': props.row.default_value === true,
                            'has-text-danger': props.row.default_value === false
                          }"
                        >
                          <b-icon
                            :icon="getTriStateIcon(props.row.default_value)"
                            :class="{
                              'has-text-grey': props.row.default_value === null || props.row.default_value === '',
                              'has-text-success': props.row.default_value === true,
                              'has-text-danger': props.row.default_value === false
                            }"
                          ></b-icon>
                          <span class="ml-2">{{ getTriStateText(props.row.default_value) }}</span>
                        </b-button>
                      </div>

                      <!-- Для типов image и id скрываем поле -->
                      <div v-else-if="props.row.type === 'image' || props.row.type === 'id'">
                        <span class="has-text-grey-light">Не требуется</span>
                      </div>

                      <!-- Для остальных типов используем обычный input -->
                      <b-input
                        v-else
                        v-model="props.row.default_value"
                        required
                      ></b-input>
                    </template>
                  </b-table-column>

                  <b-table-column label="Действия">
                    <template v-slot="props">
                      <b-button @click="removeField(props.row)" type="is-danger" size="is-small">
                        Удалить
                      </b-button>
                    </template>
                  </b-table-column>
                </b-table>
              </b-field>

              <p class="error--text" v-if="!isCreate">
                Не меняйте название полей, иначе прошлое поле будет удалено, а новое добавлено. Вы потеряете его значение
              </p>

            </div>

            <p class="help" v-if="!currentCollection.use_standard_fields">Например: field_1, field_2, field_3</p>

            <p class="error--text" v-if="isCreate">
              В вашу коллекцию будет добавлен документ с примером и выбранными полями.
              Это нужно потому что коллекция без документов не может существовать
            </p>

            <b-field v-if="isCreate" label="Использовать стандартные поля">
              <b-switch
                v-model="currentCollection.use_standard_fields">
                {{ currentCollection.use_standard_fields ? 'Да' : 'Нет' }}
              </b-switch>

              <div v-if="currentCollection.use_standard_fields" class="mt-2">
                <p class="has-text-grey is-size-7 mb-1">
                  <b-icon icon="information-outline" size="is-small"></b-icon>
                  <span class="ml-1">Стандартные поля: </span>
                  <span class="has-text-grey-dark">id, image, is_show, link, name, stars, button_text, field_1_name, field_1_value, field_2_name, field_2_value, field_3_name, field_3_value, field_4_name, field_4_value, field_5_name, field_5_value, field_6_name, field_6_value, field_7_name, field_7_value, field_8_name, field_8_value</span>
                </p>
                <p class="has-text-grey-light is-size-7">Добавляются автоматически при создании коллекции</p>
              </div>
            </b-field>
          </v-layout>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close()">Закрыть</v-btn>
        <v-btn text color="primary" v-if="currentCollection.id == null" @click="createCollection()">Добавить</v-btn>
        <v-btn text
               color="primary"
               v-if="!isCreate"
               @click="updateCollection()">Обновить</v-btn>
      </v-card-actions>
      <overlay-loader :show="isLoading"></overlay-loader>
    </v-card>
  </v-dialog>
</template>

<script>
import OverlayLoader from '@/app/shared/components/app-loader/Loader';
import { toFormData } from '@/app/shared/services/helpers';

export default {
  name: 'CollectionDetailsModal',
  components: {
    OverlayLoader,
  },
  data() {
    return {
      currentCollectionName: '',
      fieldsText: '',
    };
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.currentCollectionName = this.currentCollection.name;
      }
    },
  },
  computed: {
    isCreate() {
      return this.currentCollection.id === null;
    },
    currentCollection: {
      get() {
        return this.$store.getters.CURRENT_COLLECTION;
      },
      set(value) {
        this.$store.commit('SET_CURRENT_COLLECTION', value);
      },
    },
    isLoading: {
      get() {
        return this.$store.getters.IS_DETAIL_COLLECTION_MODAL_LOADING;
      },
      set(value) {
        this.$store.commit('SET_DETAIL_COLLECTION_MODAL_LOADING', value);
      },
    },
    isOpen: {
      get() {
        return this.$store.getters.IS_DETAIL_COLLECTION_MODAL;
      },
      set() {
        this.$store.commit('SET_DETAIL_COLLECTION_MODAL', false);
      },
    },
    idFirebase() {
      return this.$route.params.idFirebase;
    }
  },
  methods: {
    addField() {
      // Добавляет новое поле с пустым именем и типом 'string' по умолчанию
      this.currentCollection.fields.push({
        name: '',
        type: 'string',
        default_value: ''
      });
    },
    removeField(field) {
      const index = this.currentCollection.fields.indexOf(field);
      if (index > -1) {
        this.currentCollection.fields.splice(index, 1);
      }
    },
    handleClose(isOpen) {
      if (!isOpen) {
        this.close();
      }
    },
    createCollection() {
      let data = {
        collection: this.currentCollection,
        firebase: this.idFirebase
      }
      this.$store.dispatch('CREATE_COLLECTION', data)
        .then(() => {
          this.$toast.success("Коллекция создана!");
          this.$store.dispatch('GET_COLLECTIONS', {idFirebase: this.idFirebase});
          this.close();
        })
        .catch((error) => {
          if (error === 504) {
            this.$toast.error("Ошибка прокси");
          } else {
            this.$toast.error("Ошибка при создании коллекции");
          }
        });
    },
    updateCollection() {
      let data = {
        collection: this.currentCollection,
        firebase: this.idFirebase
      }
      this.$store.dispatch('UPDATE_COLLECTION', data)
        .then(() => {
          this.$toast.success("Коллекция изменена!");
          this.$store.dispatch('GET_COLLECTIONS', {idFirebase: this.idFirebase});
          this.close();
        })
        .catch((error) => {
          if (error === 504) {
            this.$toast.error("Ошибка прокси");
          } else {
            this.$toast.error("Ошибка при изменении коллекции");
          }
        });
    },
    close() {
      this.currentCollection = {
        name: '',
        id: null,
        fields: [],
        use_standard_fields: false
      };
      this.isOpen = false;
      this.isLoading = false;
    },
    toggleTriState(field) {
      if (field.default_value === true) {
        field.default_value = false;
      } else if (field.default_value === false) {
        field.default_value = null;
      } else {
        field.default_value = true;
      }
    },

    getTriStateIcon(value) {
      if (value === true) return 'check';
      if (value === false) return 'close';
      return 'minus';
    },

    getTriStateText(value) {
      if (value === true) return 'Да';
      if (value === false) return 'Нет';
      return 'Не задано';
    },
  },
};
</script>

<style>
.full-width-upload {
  width: 100%;
  display: block;
}

.full-width-upload .section {
  width: 100%;
}

.full-width-upload .content {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}
</style>
